// Planejar o trajeto (0.22.0, pedido do titular em 11/10/2026): a pessoa diz
// onde o caminhão está, aonde quer chegar (ou nada: as melhores cargas a
// partir dali) e com qual caminhão; o app monta os trajetos com 1, 2 ou 3
// cargas no caminho, pelos dados do CIOT da ANTT (24 meses, por região), e
// mostra primeiro o de maior lucro por dia. O mesmo no escritório, no app do
// motorista e no do agenciador (os números da empresa vêm do Raio-X).
// 0.22.1 (respostas do titular em 12/10/2026): a grade baixa conta a carga
// que sai a granel ou ensacada, com aviso; o frete de retorno do Norte e do
// Nordeste conta 70% do piso (a empresa muda em "Frete de retorno por
// região"), com aviso de risco.
import { planejarTrajeto, veiculos } from '../dados.js';
import { EIXOS } from '../lib/antt.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, lerInteiro, lerNumero, numeroBR, percentual } from '../lib/formato.js';
import { cidadesDaUf } from '../lib/radar.js';
import { custoDosNumeros } from '../lib/raiox.js';
import { ritmoDaEmpresa, textoDeHoras } from '../lib/modo-viagem.js';
import {
  CARROCERIAS, CARROCERIAS_DE_UM_JEITO, NOME_DO_GRUPO, PRODUTOS_ENSACAVEIS, REGIOES_DO_PAIS, TRAJETO, freteDaRegiao,
  gruposDaCarroceria, nomeDaRegiaoDoPais, planejar, textoDaRecomendacao,
} from '../lib/trajeto.js';
import { UFS } from '../lib/validacao.js';
import { ehAgenciador, ehMotorista, hojeDaEmpresa, podeGravar, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { campo, carregando, emblema, faixa, formulario, secao } from '../ui/componentes.js';
import { fonteDoEscritorio, fonteDoServidor } from './raiox.js';
import { textoCaminhao } from './viagens.js';

const CHAVE = 'hsafrota-trajeto';
function lembrado() {
  try {
    return JSON.parse(localStorage.getItem(CHAVE) ?? '{}') ?? {};
  } catch {
    return {};
  }
}
function lembrar(dados) {
  try {
    localStorage.setItem(CHAVE, JSON.stringify({ ...lembrado(), ...dados }));
  } catch {
    // Sem armazenamento (aba anônima): só não lembra.
  }
}

const RAIOS = [['50', 'Até 50 km'], ['100', 'Até 100 km'], ['200', 'Até 200 km'], ['300', 'Até 300 km']];
const opcoesDeUf = [['', 'UF'], ...UFS.map((u) => [u, u])];
const nomeDaCarroceria = (c) => CARROCERIAS.find(([v]) => v === c)?.[1] ?? null;
// Para os avisos: "contei todas para a grade baixa".
const NOME_CURTO = { carga_seca: 'a grade baixa', cacamba: 'a caçamba' };
const maiuscula = (t) => t.replace(/^./, (c) => c.toUpperCase());
const dias = (n) => `${numeroBR(n, { casas: 1 })} ${n === 1 ? 'dia' : 'dias'}`;
const km = (n) => `${numeroBR(Math.round(n))} km`;

export function telaTrajeto({ busca } = {}) {
  const deFora = ehMotorista() || ehAgenciador();
  const voltar = ehAgenciador() ? '/a/raiox' : ehMotorista() ? '/m/raiox' : '/raiox';
  // As contas são feitas ao tocar no botão: a tela não é redesenhada quando chegam dados.
  const corpo = h('div', { class: 'secao', 'data-atualiza-sozinha': '' }, carregando());
  (async () => {
    const fonte = deFora ? await fonteDoServidor() : await fonteDoEscritorio();
    trocar(corpo, ...montar({ fonte, busca }));
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo', 'Não deu para abrir o planejador'));
  });
  return casca({ titulo: 'Planejar o trajeto', voltar, conteudo: [corpo] });
}

function montar({ fonte, busca }) {
  const memoria = lembrado();
  const e = sessao.empresa;
  const { tratores } = fonte;
  const resultado = h('div', { class: 'lista', 'aria-live': 'polite' });
  const listaOrigem = h('datalist', { id: 'trajeto-cidades-origem' });
  const listaDestino = h('datalist', { id: 'trajeto-cidades-destino' });
  const preencherCidades = (lista, uf) => {
    cidadesDaUf(uf).then((nomes) => trocar(lista, ...nomes.map((n) => h('option', { value: n })))).catch(() => {});
  };
  const existe = (id) => id && tratores.some((t) => t.id === id);
  const doEndereco = busca?.get?.('caminhao') || null;
  const caminhaoInicial = [doEndereco, fonte.caminhaoPadrao, memoria.caminhao].find(existe) ?? tratores[0]?.id ?? '';
  const doCaminhao = (id) => tratores.find((t) => t.id === id) ?? null;
  const v0 = doCaminhao(caminhaoInicial);
  const origemInicial = busca?.get?.('origem') || memoria.origem || e?.cidade || '';
  const ufInicial = busca?.get?.('uf') || memoria.uf_origem || e?.uf || '';
  const reservaInicial = fonte.preferencias.reserva ?? memoria.reserva ?? lembradoDoRaiox().reserva ?? null;
  // O ritmo e as esperas que as viagens da empresa ensinaram (modo viagem, 0.23.0).
  const aprendido = fonte.numerosIniciais?.aprendido ?? null;
  const ritmoInicial = ritmoDaEmpresa(aprendido);
  // O frete de retorno por região: o escritório muda; o motorista e o agenciador usam o da empresa.
  const freteInicial = fonte.freteRegiao ?? freteDaRegiao(null);
  const mudaRegiao = Boolean(fonte.guardarFreteRegiao) && podeGravar();

  const guardar = podeGravar() && campo({
    rotulo: 'Guardar a carroceria e os eixos no cadastro do caminhão', nome: 'guardar', tipo: 'caixa',
    valor: Boolean(v0 && (!v0.carroceria || !v0.eixos)),
  });
  const form = formulario({
    filhos: [
      h('p', { class: 'pequeno' },
        'Diga onde o caminhão está e aonde quer chegar: o app monta os trajetos com 1, 2 ou 3 cargas no caminho e mostra primeiro o que dá mais lucro por dia, não o mais curto.'),
      h('fieldset', { class: 'campo' },
        h('legend', null, 'Onde o caminhão está'),
        h('div', { class: 'grade-uf' },
          campo({ rotulo: 'Cidade', nome: 'origem', obrigatorio: true, formato: 'nome', valor: origemInicial,
            atributos: { list: 'trajeto-cidades-origem', autocomplete: 'off' } }),
          campo({ rotulo: 'UF', nome: 'uf_origem', tipo: 'select', obrigatorio: true, valor: ufInicial, opcoes: opcoesDeUf,
            aoMudar: (ev) => preencherCidades(listaOrigem, ev.target.value) })),
        listaOrigem),
      h('fieldset', { class: 'campo' },
        h('legend', null, 'Aonde quer chegar'),
        h('p', { class: 'dica' }, 'Em branco: as melhores cargas a partir de onde o caminhão está.'),
        h('div', { class: 'grade-uf' },
          campo({ rotulo: 'Cidade', nome: 'destino', formato: 'nome', valor: memoria.destino ?? '',
            atributos: { list: 'trajeto-cidades-destino', autocomplete: 'off' } }),
          campo({ rotulo: 'UF', nome: 'uf_destino', tipo: 'select', valor: memoria.uf_destino ?? '', opcoes: opcoesDeUf,
            aoMudar: (ev) => preencherCidades(listaDestino, ev.target.value) })),
        listaDestino,
        campo({ rotulo: 'Chegar até quantos km de lá', nome: 'raio', tipo: 'select', valor: String(memoria.raio ?? 100), opcoes: RAIOS })),
      tratores.length > 0 && campo({
        rotulo: 'Caminhão', nome: 'caminhao', tipo: 'select', valor: caminhaoInicial,
        opcoes: tratores.map((t) => [t.id, textoCaminhao(t)]),
      }),
      campo({
        rotulo: 'Carroceria', nome: 'carroceria', tipo: 'select', obrigatorio: true, valor: v0?.carroceria ?? memoria.carroceria ?? '',
        opcoes: [['', 'Escolha a carroceria'], ...CARROCERIAS], dica: 'O app conta só a carga que essa carroceria leva.',
      }),
      campo({
        rotulo: 'Eixos carregados (caminhão + carreta)', nome: 'eixos', tipo: 'select', valor: String(v0?.eixos ?? memoria.eixos ?? 5),
        opcoes: EIXOS.map((n) => [String(n), `${n} eixos`]),
      }),
      guardar,
      campo({ rotulo: 'Reserva para manutenção e pneus (R$ por km)', nome: 'reserva', obrigatorio: true, formato: 'dinheiro',
        valor: reservaInicial !== null && reservaInicial !== undefined ? numeroBR(reservaInicial, { casas: 2 }) : '',
        dica: fonte.dicaDaReserva, atributos: { inputmode: 'decimal' } }),
      h('details', { class: 'mais-detalhes' },
        h('summary', null, 'Ritmo, consumo e preço do diesel'),
        campo({ rotulo: 'Km rodados por dia', nome: 'km_dia', formato: 'km', valor: numeroBR(memoria.km_dia ?? ritmoInicial.kmPorDia),
          dica: aprendido?.kmPorDia
            ? `A média de um dia de viagem, com as paradas. Nas suas últimas ${aprendido.viagens} viagens (modo viagem): ${numeroBR(aprendido.kmPorDia)} km por dia.`
            : 'A média de um dia de viagem, com as paradas.',
          atributos: { inputmode: 'numeric' } }),
        campo({ rotulo: 'Consumo (km/l)', nome: 'consumo', formato: 'percentual', dica: 'Em branco: a média do caminhão (ou da frota), como no Raio-X.',
          atributos: { inputmode: 'decimal' } }),
        campo({ rotulo: 'Preço do diesel (R$ por litro)', nome: 'diesel', formato: 'dinheiro', dica: 'Em branco: a média paga nos últimos 30 dias.',
          atributos: { inputmode: 'decimal' } })),
      mudaRegiao && h('details', { class: 'mais-detalhes', dataset: { bloco: 'frete-regiao' } },
        h('summary', null, 'Frete de retorno por região'),
        h('p', { class: 'dica' },
          'Onde o frete de retorno costuma sair abaixo do piso da ANTT (com documentação burlada), quanto do piso o app conta para a carga que sai de lá. Em branco: o que a empresa costuma receber. Vale também no app do motorista e no do agenciador.'),
        ...REGIOES_DO_PAIS.map(([sigla, nome]) => campo({
          rotulo: `${nome} (% do piso)`, nome: `regiao_${sigla}`, formato: 'percentual',
          valor: freteInicial[sigla] !== null && freteInicial[sigla] !== undefined ? String(freteInicial[sigla]) : '',
          atributos: { inputmode: 'numeric' },
        }))),
    ],
    textoBotao: 'Planejar o trajeto',
    textoEnviando: 'Planejando…',
    validar: (d) => {
      const erros = {};
      if (!d.carroceria) erros.carroceria = 'Escolha a carroceria: o app conta só a carga que ela leva.';
      if (Boolean(d.destino?.trim()) !== Boolean(d.uf_destino)) {
        erros[d.destino?.trim() ? 'uf_destino' : 'destino'] = 'Preencha a cidade e a UF do destino (ou deixe os dois em branco).';
      }
      const reserva = lerNumero(d.reserva);
      if (reserva === null) erros.reserva = 'Informe quanto separa por km para manutenção e pneus (pode ser 0).';
      else if (!(reserva >= 0 && reserva <= 20)) erros.reserva = 'Confira o valor: é por km rodado (ex.: 0,80).';
      const kmDia = lerInteiro(d.km_dia);
      if (d.km_dia && !(kmDia >= 200 && kmDia <= 1200)) erros.km_dia = 'Use um número de 200 a 1.200.';
      const consumo = lerNumero(d.consumo);
      if (consumo !== null && !(consumo > 0 && consumo <= 10)) erros.consumo = 'Confira o consumo: quantos km o caminhão faz com 1 litro.';
      const diesel = lerNumero(d.diesel);
      if (diesel !== null && !(diesel >= 1 && diesel <= 20)) erros.diesel = 'Confira o preço do litro.';
      for (const [sigla] of REGIOES_DO_PAIS) {
        const pct = d[`regiao_${sigla}`] === undefined ? null : lerNumero(d[`regiao_${sigla}`]);
        if (pct !== null && !(pct >= 20 && pct <= 200)) {
          erros[`regiao_${sigla}`] = 'Use de 20 a 200 (% do piso), ou deixe em branco.';
          form.querySelector('[data-bloco="frete-regiao"]')?.setAttribute('open', '');
        }
      }
      return erros;
    },
    aoEnviar: async (d) => {
      const reserva = lerNumero(d.reserva);
      const kmPorDia = lerInteiro(d.km_dia) || TRAJETO.kmPorDia;
      const destino = d.destino?.trim() || null;
      lembrar({
        origem: d.origem, uf_origem: d.uf_origem, destino, uf_destino: destino ? d.uf_destino : '', raio: Number(d.raio),
        caminhao: d.caminhao || null, carroceria: d.carroceria, eixos: Number(d.eixos), km_dia: kmPorDia, reserva,
      });
      const caminhao = doCaminhao(d.caminhao);
      if (d.guardar && caminhao && podeGravar()) {
        await veiculos.atualizar(caminhao.id, { carroceria: d.carroceria, eixos: Number(d.eixos) }, caminhao);
      }
      const numeros = await fonte.numeros();
      const custo = custoDosNumeros(numeros, {
        veiculoId: d.caminhao || null, reserva, consumo: lerNumero(d.consumo), diesel: lerNumero(d.diesel),
      });
      // No escritório, guarda a reserva e os números da empresa (como o Raio-X).
      fonte.aoCalcular(numeros, { reserva, sobra: fonte.preferencias.sobra ?? 30 });
      let freteRegiao = freteInicial;
      if (mudaRegiao) {
        freteRegiao = freteDaRegiao(Object.fromEntries(REGIOES_DO_PAIS.map(([sigla]) => {
          const pct = lerNumero(d[`regiao_${sigla}`]);
          return [sigla, pct === null ? null : Math.round(pct)];
        })));
        fonte.guardarFreteRegiao(freteRegiao);
      }
      if (!custo.porKm) {
        form.querySelector('[name="consumo"]')?.closest('details')?.setAttribute('open', '');
        form.querySelector('[name="consumo"]')?.focus();
        throw new Error('CONSUMO: ainda sem o consumo do caminhão. Digite o consumo (km/l) em "Ritmo, consumo e preço do diesel".');
      }
      const mapa = await planejarTrajeto({
        origemCidade: d.origem, origemUf: d.uf_origem, destinoCidade: destino, destinoUf: destino ? d.uf_destino : null,
        raioKm: Number(d.raio) || 100, grupos: gruposDaCarroceria(d.carroceria),
      });
      if (mapa?.erro === 'ORIGEM') throw new Error('ORIGEM: não achei a cidade onde o caminhão está. Confira o nome e a UF.');
      if (mapa?.erro === 'DESTINO') throw new Error('DESTINO: não achei a cidade de destino. Confira o nome e a UF.');
      const ritmo = ritmoDaEmpresa(numeros?.aprendido);
      const r = planejar(mapa, {
        carroceria: d.carroceria, eixos: Number(d.eixos), custo, kmPorDia, mes: `${hojeDaEmpresa().slice(0, 7)}-01`, freteRegiao,
        esperaCarregar: ritmo.esperaCarregar, esperaDescarregar: ritmo.esperaDescarregar,
      });
      trocar(resultado, ...blocosDoResultado({ r, mapa, d, custo, mudaRegiao }));
      resultado.scrollIntoView({ block: 'start', behavior: 'smooth' });
    },
  });

  if (ufInicial) preencherCidades(listaOrigem, ufInicial);
  if (memoria.uf_destino) preencherCidades(listaDestino, memoria.uf_destino);
  // Ao trocar de caminhão, a carroceria e os eixos do cadastro dele
  form.querySelector('[name="caminhao"]')?.addEventListener('change', (ev) => {
    const v = doCaminhao(ev.target.value);
    if (v?.carroceria) form.querySelector('[name="carroceria"]').value = v.carroceria;
    if (v?.eixos) form.querySelector('[name="eixos"]').value = String(v.eixos);
    const caixa = form.querySelector('[name="guardar"]');
    if (caixa) caixa.checked = Boolean(v && (!v.carroceria || !v.eixos));
  });
  return [form, resultado];
}

// A reserva lembrada pelo Raio-X neste aparelho (quando a empresa ainda não tem).
function lembradoDoRaiox() {
  try {
    return JSON.parse(localStorage.getItem('hsafrota-raiox') ?? '{}') ?? {};
  } catch {
    return {};
  }
}

function textoDoPreco(p) {
  if (p.fonte === 'empresa') return `o que a empresa recebeu nesse trecho: ${dinheiro(p.porKm)} por km, em ${p.cargas} fretes`;
  if (p.fonte === 'regiao') {
    const nome = nomeDaRegiaoDoPais(p.regiao);
    return p.pct < 100
      ? `${p.pct}% do piso da ANTT, pelo histórico do frete de retorno no ${nome}`
      : `${p.pct}% do piso da ANTT, como a empresa definiu para o ${nome}`;
  }
  return `piso da ANTT${p.fator !== 1 ? ` × ${numeroBR(p.fator, { casas: 2 })}` : ''}`;
}

// Os avisos do trecho: o frete de retorno abaixo do piso e a carga que pode sair a granel ou ensacada.
function avisosDoTrecho(t, carroceria) {
  const avisos = [];
  if (t.abaixoDoPiso) {
    avisos.push(h('br'), h('strong', null, 'Aviso de risco: '),
      `saindo do ${t.abaixoDoPiso.regiao}, o frete de retorno costuma sair abaixo do piso, com documentação burlada. `,
      t.preco.fonte === 'regiao'
        ? `Foi considerado ${t.preco.pct}% do piso, pelo histórico da região.`
        : 'Aqui vale o que a empresa já recebeu nesse trecho.');
  }
  if (t.ensacada > 0) {
    avisos.push(h('br'), h('strong', null, 'Atenção: '),
      `${Math.round(t.ensacada * 100)}% destas cargas são de ${PRODUTOS_ENSACAVEIS}, que saem a granel ou ensacados (a ANTT não diz qual). `,
      `Contei todas para ${NOME_CURTO[carroceria] ?? 'esta carroceria'}: a chance de achar carga pode ser menor.`);
  }
  return avisos;
}

function linhaDoTrecho(t, carroceria) {
  const preco = textoDoPreco(t.preco);
  // Nunca 100%: é uma estimativa.
  const chance = Math.min(99, Math.round(t.chance * 100));
  return h('li', null,
    h('strong', null, `${t.de} → ${t.para}: ${km(t.km)}`),
    h('br'),
    `Frete estimado: ${dinheiro(t.preco.valor)} (${preco}).`,
    ...avisosDoTrecho(t, carroceria),
    h('br'),
    `Saem uns ${numeroBR(Math.round(t.volume))} por mês${t.grupo ? ` (${NOME_DO_GRUPO[t.grupo]})` : ''}${t.tendencia ? `, ${t.tendencia}` : ''}`,
    ` · chance de achar a carga em até ${TRAJETO.diasDaChance} dias: ${chance}% · espera de ${dias(Math.round(t.espera * 10) / 10)}.`,
    t.kmVazio > 0 && [h('br'), `Antes, ${km(t.kmVazio)} vazio até a carga.`],
    h('br'),
    `Sobra no trecho (se pegar a carga): ${dinheiro(t.lucro)}.`);
}

function cartaoDaRota(rota, posicao, { destino, carroceria }) {
  return h('div', { class: `cartao trajeto${posicao === 0 ? ' destaque' : ''}` }, h('div', { class: 'cartao-corpo' },
    h('span', { class: 'cartao-linha' }, `${posicao + 1}º · nota ${rota.nota}`),
    h('span', { class: 'cartao-titulo' }, rota.nomes.join(' → ')),
    h('span', { class: 'cartao-linha' },
      `Lucro esperado: ${dinheiro(rota.lucroEsperado)} em ${dias(rota.dias)} · ${dinheiro(rota.lucroPorDia)} por dia`),
    h('span', { class: 'cartao-linha' }, `${km(rota.kmCarregado)} carregado · ${km(rota.kmVazio)} vazio`),
    rota.vazioFinal > 0 && destino && h('span', { class: 'cartao-linha' }, `No fim, ${km(rota.vazioFinal)} vazio até perto de ${destino.nome}/${destino.uf}.`),
    rota.chance < 0.95 && h('span', { class: 'cartao-linha' }, `Se pegar todas as cargas: ${dinheiro(rota.lucroSePegar)}.`),
    h('span', { class: 'cartao-emblemas' },
      emblema(`Risco ${rota.risco}`, rota.risco === 'baixo' ? 'ok' : rota.risco === 'médio' ? 'alerta' : 'perigo'),
      rota.direta && emblema('Carga direta', 'info'),
      rota.abaixoDoPiso && emblema('Frete abaixo do piso', 'alerta'),
      rota.ensacada && emblema('Granel ou ensacada', 'alerta')),
    h('details', { class: 'mais-detalhes' },
      h('summary', null, 'Ver os trechos'),
      h('ul', { class: 'lista-simples pequeno' }, rota.trechos.map((t) => linhaDoTrecho(t, carroceria))))));
}

function comoFoiFeito({ r, mapa, d, custo, mudaRegiao }) {
  const partes = [];
  partes.push(r.fator.fonte === 'empresa'
    ? `Frete de cada trecho: o piso da ANTT (tabela A, ${d.eixos} eixos, o tipo de carga que mais sai) × ${numeroBR(r.fator.fator, { casas: 2 })}: o quanto a empresa recebeu acima do piso nos últimos 12 meses (${r.fator.cargas} fretes). Onde a empresa já rodou (2 fretes ou mais), vale o R$/km que ela recebeu.`
    : `Frete de cada trecho: o piso da ANTT (tabela A, ${d.eixos} eixos, o tipo de carga que mais sai). Com 3 fretes ou mais da empresa no último ano, o app ajusta pelo que ela costuma receber acima do piso.`);
  const comPct = REGIOES_DO_PAIS.filter(([sigla]) => r.freteRegiao?.[sigla]);
  if (comPct.length > 0) {
    const lista = comPct.map(([sigla, nome]) => `${nome}: ${r.freteRegiao[sigla]}%`).join('; ');
    partes.push(`Frete que sai de cada região do país, em % do piso da ANTT (${lista}). Onde o frete de retorno costuma sair abaixo do piso, com documentação burlada (abaixo de 100%), o trecho tem aviso de risco e o risco do trajeto fica pelo menos médio. Onde a empresa já rodou, vale o que ela recebeu. ${mudaRegiao ? 'Para mudar: "Frete de retorno por região", acima.' : 'Quem define é a empresa.'}`);
  }
  if (CARROCERIAS_DE_UM_JEITO.includes(d.carroceria)) {
    partes.push(`${maiuscula(PRODUTOS_ENSACAVEIS)} saem a granel ou ensacados, e a ANTT não diz qual: contam todos para ${NOME_CURTO[d.carroceria]}. Quando essas cargas são ${Math.round(TRAJETO.avisoEnsacada * 100)}% ou mais do trecho, ele tem um aviso.`);
  }
  partes.push(`Custo: ${dinheiro(r.porKm)} por km rodado, carregado ou vazio (diesel, despesas da viagem e a reserva, como no Raio-X)${custo.pctDoFrete ? ` e ${percentual(custo.pctDoFrete * 100)} do frete para o motorista e o agenciamento` : ''}. Os custos fixos ficam de fora.`);
  const esperas = r.esperaCarregar === TRAJETO.diasParaCarregar && r.esperaDescarregar === TRAJETO.diasParaDescarregar
    ? 'meio dia para carregar e meio para descarregar'
    : `${textoDeHoras(r.esperaCarregar * 24)} para carregar e ${textoDeHoras(r.esperaDescarregar * 24)} para descarregar (a média das suas viagens, pelo modo viagem)`;
  partes.push(`Tempo: ${numeroBR(lerInteiro(d.km_dia) || TRAJETO.kmPorDia)} km por dia, ${esperas} e a espera pela carga: quanto menos carga sai para o próximo lugar, mais espera (até ${TRAJETO.esperaMaxima} dias).`);
  partes.push(`Chance de carga: pelas cargas que saem por mês (dados abertos do CIOT da ANTT, com a sazonalidade do mês e a tendência), contando que o caminhão consegue a metade delas (a disputa com os outros). O lucro de cada trecho entra multiplicado por essa chance.`);
  partes.push(`Carroceria: ${nomeDaCarroceria(d.carroceria) ?? 'todas'}; conta só a carga que ela leva (pelo tipo de carga do CIOT, aproximado).`);
  partes.push('Risco: pela menor chance de achar carga nos trechos (85% ou mais: baixo; de 50% a 85%: médio; abaixo de 50%: alto).');
  partes.push('Nota: o lucro esperado por dia, comparado com o melhor trajeto (100).');
  if (mapa.serie?.inicio) {
    partes.push(`Dados do CIOT de ${data(mapa.serie.inicio).slice(3)} a ${data(mapa.serie.fim).slice(3)} (${mapa.serie.meses} meses), por região do IBGE.`);
  }
  return h('ul', { class: 'lista-simples pequeno' }, partes.map((t) => h('li', null, t)));
}

function blocosDoResultado({ r, mapa, d, custo, mudaRegiao }) {
  if (!mapa?.pronto) {
    return [faixa('O planejador ainda está sendo montado: os dados da ANTT chegam uma vez por mês.', 'info', 'Ainda sem os dados')];
  }
  const destino = mapa.destino;
  if (r.rotas.length === 0) {
    return [faixa(`Não achei trajeto com carga para ${nomeDaCarroceria(d.carroceria)?.toLowerCase() ?? 'esse caminhão'} saindo de ${d.origem}/${d.uf_origem}${destino ? ` até perto de ${destino.nome}/${destino.uf}` : ''}. Tente um raio maior ou outra cidade de partida.`,
      'alerta', 'Nenhum trajeto'),
    comoFoiFeito({ r, mapa, d, custo, mudaRegiao })];
  }
  const [melhor] = r.rotas;
  return [
    faixa(`${melhor.nomes.join(' → ')}: ${dinheiro(melhor.lucroPorDia)} por dia.`, 'ok', `Recomendação: ${textoDaRecomendacao(melhor).replace(/^./, (c) => c.toLowerCase())}`),
    r.vazioDireto && destino && h('p', { class: 'pequeno' },
      `Para comparar: ir vazio até perto de ${destino.nome}/${destino.uf} são ${km(r.vazioDireto.km)}, uns ${dias(r.vazioDireto.dias)} e ${dinheiro(r.vazioDireto.custo)} de custo.`),
    secao('Melhores trajetos', h('div', { class: 'lista' }, r.rotas.map((rota, i) => cartaoDaRota(rota, i, { destino, carroceria: d.carroceria }))),
      { contador: String(r.rotas.length) }),
    secao('Como foi feita a conta', comoFoiFeito({ r, mapa, d, custo, mudaRegiao })),
  ];
}
